import { useState, useEffect } from 'react'

const TOUR_KEY = 'bst-tour-done-v1'

interface Step {
  title: string
  body: string
  anchor: string
  placement: 'bottom' | 'left' | 'right' | 'top'
}

const STEPS: Step[] = [
  {
    title: 'Добро пожаловать в BST! 🎉',
    body: 'Это ваш редактор сайтов. Давайте за 30 секунд покажем, как всё работает.',
    anchor: '.editor-logo',
    placement: 'bottom',
  },
  {
    title: 'Библиотека блоков',
    body: 'Здесь 115+ блоков: заголовки, изображения, формы, товары, карты и многое другое. Перетащите любой блок на холст.',
    anchor: '[data-tour="block-library"]',
    placement: 'right',
  },
  {
    title: 'Холст редактора',
    body: 'Нажмите на любой блок — откроется панель свойств. Двойной клик — редактирование текста прямо на холсте.',
    anchor: '.editor-canvas',
    placement: 'left',
  },
  {
    title: 'Страницы сайта',
    body: 'Добавляйте неограниченное количество страниц: лендинг, блог, магазин, контакты. Каждая страница — отдельный URL.',
    anchor: '[data-tour="pages-tab"]',
    placement: 'bottom',
  },
  {
    title: 'Настройки и SEO',
    body: 'Настройте SEO, аналитику, домен, PWA и интеграции. Аудит SEO проверит все страницы сразу.',
    anchor: '[data-tour="settings-btn"]',
    placement: 'bottom',
  },
  {
    title: 'Публикация одним кликом',
    body: 'Когда сайт готов — нажмите «Опубликовать». Сайт появится по адресу yoursite.mybst.ru мгновенно.',
    anchor: '.btn-primary',
    placement: 'bottom',
  },
]

function getAnchorRect(selector: string): DOMRect | null {
  const el = document.querySelector(selector)
  return el ? el.getBoundingClientRect() : null
}

export function OnboardingTour() {
  const [step, setStep] = useState(0)
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const [arrowSide, setArrowSide] = useState<'top' | 'bottom' | 'left' | 'right'>('top')

  useEffect(() => {
    if (localStorage.getItem(TOUR_KEY)) return
    const timer = setTimeout(() => setVisible(true), 1200)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!visible) return
    updatePosition()
  }, [visible, step])

  function updatePosition() {
    const current = STEPS[step]
    const rect = getAnchorRect(current.anchor)
    if (!rect) {
      setPos({ top: window.innerHeight / 2 - 100, left: window.innerWidth / 2 - 160 })
      setArrowSide('top')
      return
    }

    const TIP_W = 320
    const TIP_H = 180
    const MARGIN = 12

    if (current.placement === 'bottom') {
      setPos({ top: rect.bottom + MARGIN, left: Math.min(rect.left, window.innerWidth - TIP_W - MARGIN) })
      setArrowSide('top')
    } else if (current.placement === 'top') {
      setPos({ top: rect.top - TIP_H - MARGIN, left: Math.min(rect.left, window.innerWidth - TIP_W - MARGIN) })
      setArrowSide('bottom')
    } else if (current.placement === 'right') {
      setPos({ top: rect.top, left: rect.right + MARGIN })
      setArrowSide('left')
    } else {
      setPos({ top: rect.top, left: rect.left - TIP_W - MARGIN })
      setArrowSide('right')
    }
  }

  function finish() {
    localStorage.setItem(TOUR_KEY, '1')
    setVisible(false)
  }

  function next() {
    if (step < STEPS.length - 1) setStep(s => s + 1)
    else finish()
  }

  function prev() {
    if (step > 0) setStep(s => s - 1)
  }

  if (!visible) return null

  const current = STEPS[step]
  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    width: 0, height: 0,
    ...(arrowSide === 'top' ? { top: -8, left: 20, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid #1e293b' } : {}),
    ...(arrowSide === 'bottom' ? { bottom: -8, left: 20, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid #1e293b' } : {}),
    ...(arrowSide === 'left' ? { left: -8, top: 16, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: '8px solid #1e293b' } : {}),
    ...(arrowSide === 'right' ? { right: -8, top: 16, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '8px solid #1e293b' } : {}),
  }

  return (
    <>
      {/* Backdrop */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }} />

      {/* Tooltip */}
      <div
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: 320,
          background: '#1e293b',
          color: '#f1f5f9',
          borderRadius: '12px',
          padding: '20px',
          zIndex: 9999,
          boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
          fontFamily: 'inherit',
        }}
      >
        <div style={arrowStyle} />

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '5px', marginBottom: '14px' }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? '20px' : '6px', height: '6px', borderRadius: '3px',
              background: i === step ? '#5b6af0' : '#334155',
              transition: 'width 0.2s',
            }} />
          ))}
        </div>

        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '8px', color: '#f1f5f9' }}>
          {current.title}
        </div>
        <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '16px' }}>
          {current.body}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={finish}
            style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '12px', cursor: 'pointer', padding: 0 }}
          >Пропустить</button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {step > 0 && (
              <button onClick={prev} style={{ padding: '7px 14px', background: '#334155', color: '#f1f5f9', border: 'none', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>
                ← Назад
              </button>
            )}
            <button
              onClick={next}
              style={{ padding: '7px 18px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
            >
              {step === STEPS.length - 1 ? '🚀 Начать' : 'Далее →'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
